import { getUrlFYear } from 'legacy/lut/features/partner360/configuration/helpers/getUrlFYear'
import {
  selectJBPSettingsCurrentFYear,
  useAvailableFYears,
  useJbpSettings,
} from 'legacy/lut/features/partner360/jbpSettings'
import { history } from 'legacy/lut/helpers/route'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { getUrlWithParams } from 'core-components/helpers/url'
import { clampCockpitFYear } from 'partner360/features/financialCockpit/lib'
import { setCockpitFYear, type TCockpitMode } from 'partner360/features/financialCockpit/model'

/**
 * Settles the cockpit's fiscal year and tells whether it is settled, so the view requests its data
 * only after that. The mode comes from the view rather than the store: the store learns it from the
 * request itself, so waiting for it sent the first request with the global fallback year and a
 * second one once the year was settled. Being settled is kept per opening, not in the store: a
 * cockpit opening while another one is still leaving would find the other one's year there.
 */
export const useInitCockpitFYear = (mode: Exclude<TCockpitMode, null>, isEnabled: boolean) => {
  const dispatch = useAppDispatch()
  const currentFYear = useSelector(selectJBPSettingsCurrentFYear)
  const availableFYears = useAvailableFYears()
  const [isFYearResolved, setIsFYearResolved] = useState(false)

  // Loading waits for the settled year, so the JBP settings it is taken from must arrive.
  useJbpSettings(false)

  useEffect(() => {
    if (!isEnabled) {
      setIsFYearResolved(false)
      return
    }

    if (!currentFYear) {
      return
    }

    if (mode === 'partner') {
      const urlFYear = getUrlFYear()

      if (urlFYear) {
        const clampedFYear = clampCockpitFYear(urlFYear, currentFYear, availableFYears)

        dispatch(setCockpitFYear(clampedFYear))

        if (clampedFYear !== urlFYear) {
          history.push(getUrlWithParams({ fyear: clampedFYear }))
        }
      } else {
        const today = new Date()
        const year = currentFYear || today.getFullYear()
        dispatch(setCockpitFYear(year))
        history.push(getUrlWithParams({ fyear: year }))
      }
    } else {
      // manager mode: skip URL
      dispatch(setCockpitFYear(currentFYear))
    }

    setIsFYearResolved(true)
  }, [mode, isEnabled, currentFYear, availableFYears, dispatch])

  return isFYearResolved
}
